import { parsePhoneNumber as parseE164 } from 'react-phone-number-input';

/**
 * Ensure dial code has a leading + and digits only after it.
 * @param {string|number|null|undefined} code
 * @returns {string} e.g. "+971" or ""
 */
export const normalizeDialCode = (code) => {
    if (code == null || code === '') return '';
    const digits = String(code).replace(/[^\d]/g, '');
    if (!digits) return '';
    return `+${digits}`;
};

/**
 * Strip non-digits from a national number string.
 * @param {string|number|null|undefined} national
 * @returns {string}
 */
export const normalizeNationalNumber = (national) => {
    if (national == null || national === '') return '';
    return String(national).replace(/[^\d]/g, '');
};

/**
 * If national digits already start with the dial-code digits (full intl pasted as national),
 * strip the dial-code prefix when the remainder is a plausible national number.
 * @param {string} nationalDigits
 * @param {string} phoneCode normalized (+971)
 * @returns {string}
 */
const stripDialCodeFromNational = (nationalDigits, phoneCode) => {
    if (!nationalDigits || !phoneCode) return nationalDigits || '';
    const codeDigits = phoneCode.replace(/[^\d]/g, '');
    if (!codeDigits) return nationalDigits;
    if (nationalDigits.startsWith(codeDigits)) {
        const remainder = nationalDigits.slice(codeDigits.length);
        if (remainder.length >= 6) {
            return remainder;
        }
    }
    return nationalDigits;
};

const findCountryByPhoneCode = (masterCountries = [], phoneCode) => {
    const normalized = normalizeDialCode(phoneCode);
    if (!normalized || !masterCountries?.length) return null;
    return (
        masterCountries.find((c) => normalizeDialCode(c.phoneCode) === normalized) ||
        masterCountries.find((c) => String(c.phoneCode) === String(phoneCode)) ||
        null
    );
};

const findCountryByIso = (masterCountries = [], isoCode) => {
    if (!isoCode || !masterCountries?.length) return null;
    const upper = String(isoCode).toUpperCase();
    return masterCountries.find((c) => c.isoCode?.toUpperCase() === upper) || null;
};

/**
 * Format address phone as "+{dial} {national}" (canonical cart/order shape).
 * @param {{ phoneCode?: string, national?: string }} parts
 * @returns {string}
 */
export const formatAddressPhone = ({ phoneCode, national } = {}) => {
    const code = normalizeDialCode(phoneCode);
    let nationalDigits = normalizeNationalNumber(national);
    nationalDigits = stripDialCodeFromNational(nationalDigits, code);
    // UK numbers are written with a trunk 0 (07700 900123); it is not part of the +44 number.
    if (code === '+44' && nationalDigits.startsWith('0')) nationalDigits = nationalDigits.slice(1);
    if (!code || !nationalDigits) return '';
    return `${code} ${nationalDigits}`;
};

/**
 * Robust parse of stored address phone strings into parts.
 * Handles spaced "+971 50…", E.164 "+97150…", and national-only with fallbacks.
 * @param {string|null|undefined} raw
 * @param {{ masterCountries?: Array, fallbackCountryCode?: string, fallbackPhoneCode?: string }} options
 * @returns {{ phoneNumber: string, phoneCode: string, countryCode: string }}
 */
export const parseAddressPhone = (raw, options = {}) => {
    const { masterCountries = [], fallbackCountryCode = '', fallbackPhoneCode = '' } = options;

    if (!raw) {
        return { phoneNumber: '', phoneCode: '', countryCode: '' };
    }

    const trimmed = String(raw).trim();
    if (!trimmed) {
        return { phoneNumber: '', phoneCode: '', countryCode: '' };
    }

    // Spaced dial + national (canonical): "+971 501234567"
    if (/\s/.test(trimmed)) {
        const parts = trimmed.split(/\s+/).filter(Boolean);
        const phoneCode = normalizeDialCode(parts[0]);
        let phoneNumber = normalizeNationalNumber(parts.slice(1).join(''));
        phoneNumber = stripDialCodeFromNational(phoneNumber, phoneCode);
        const matched = findCountryByPhoneCode(masterCountries, phoneCode);
        return {
            phoneNumber,
            phoneCode,
            countryCode: matched?.isoCode || fallbackCountryCode || '',
        };
    }

    // E.164 / concatenated international
    const e164Candidate = trimmed.startsWith('+') ? trimmed : trimmed.startsWith('00') ? `+${trimmed.slice(2)}` : null;
    if (e164Candidate) {
        try {
            const parsed = parseE164(e164Candidate);
            if (parsed?.nationalNumber && parsed?.countryCallingCode) {
                return {
                    phoneNumber: parsed.nationalNumber,
                    phoneCode: normalizeDialCode(parsed.countryCallingCode),
                    countryCode: parsed.country || fallbackCountryCode || '',
                };
            }
        } catch {
            // fall through
        }
    }

    // Digits-only that may be full intl without +
    const digitsOnly = normalizeNationalNumber(trimmed);
    if (digitsOnly.length >= 10) {
        try {
            const parsed = parseE164(`+${digitsOnly}`);
            if (parsed?.nationalNumber && parsed?.countryCallingCode) {
                // Only accept if parsing meaningfully split (national shorter than full)
                if (parsed.nationalNumber.length < digitsOnly.length) {
                    return {
                        phoneNumber: parsed.nationalNumber,
                        phoneCode: normalizeDialCode(parsed.countryCallingCode),
                        countryCode: parsed.country || fallbackCountryCode || '',
                    };
                }
            }
        } catch {
            // fall through
        }
    }

    // National-only: resolve dial code from fallbacks
    let phoneCode = normalizeDialCode(fallbackPhoneCode);
    let countryCode = fallbackCountryCode || '';
    if (!phoneCode && fallbackCountryCode) {
        const country = findCountryByIso(masterCountries, fallbackCountryCode);
        phoneCode = normalizeDialCode(country?.phoneCode);
        countryCode = country?.isoCode || fallbackCountryCode;
    }

    let phoneNumber = stripDialCodeFromNational(digitsOnly, phoneCode);

    return {
        phoneNumber,
        phoneCode,
        countryCode,
    };
};

/**
 * Extract dial code + national from auth/customer Redux (or Firebase minimal session).
 * @param {object|null|undefined} authData
 * @param {Array} masterCountries
 * @returns {{ phoneCode: string, national: string, countryCode: string }}
 */
export const getAuthPhoneParts = (authData, masterCountries = []) => {
    if (!authData) {
        return { phoneCode: '', national: '', countryCode: '' };
    }

    const authPhone = authData.phoneNumber;
    const authCode = authData.phoneCode;
    const authCountry = authData.countryCode || '';

    // Minimal Firebase session: full E.164 on phoneNumber
    if (authPhone && String(authPhone).trim().startsWith('+')) {
        try {
            const parsed = parseE164(String(authPhone).trim());
            if (parsed?.nationalNumber && parsed?.countryCallingCode) {
                return {
                    phoneCode: normalizeDialCode(parsed.countryCallingCode),
                    national: parsed.nationalNumber,
                    countryCode: parsed.country || authCountry,
                };
            }
        } catch {
            // fall through
        }
    }

    // Customer doc: phoneCode (often without +) + national phoneNumber
    if (authPhone && authCode) {
        const phoneCode = normalizeDialCode(authCode);
        let national = normalizeNationalNumber(authPhone);
        national = stripDialCodeFromNational(national, phoneCode);
        return {
            phoneCode,
            national,
            countryCode: authCountry,
        };
    }

    // National + ISO country → look up dial code
    if (authPhone && authCountry) {
        const country = findCountryByIso(masterCountries, authCountry);
        if (country?.phoneCode) {
            const phoneCode = normalizeDialCode(country.phoneCode);
            let national = normalizeNationalNumber(authPhone);
            national = stripDialCodeFromNational(national, phoneCode);
            return {
                phoneCode,
                national,
                countryCode: authCountry,
            };
        }
    }

    if (authPhone) {
        return {
            phoneCode: normalizeDialCode(authCode) || '',
            national: normalizeNationalNumber(authPhone),
            countryCode: authCountry,
        };
    }

    return { phoneCode: '', national: '', countryCode: authCountry };
};

/**
 * True when formatted phone has a + dial code and a national number.
 * @param {string|null|undefined} formatted
 * @returns {boolean}
 */
export const isValidAddressPhone = (formatted) => {
    if (!formatted || typeof formatted !== 'string') return false;
    const parsed = parseAddressPhone(formatted);
    return Boolean(
        parsed.phoneCode && parsed.phoneCode.startsWith('+') && parsed.phoneNumber && parsed.phoneNumber.length >= 6
    );
};

/**
 * Normalize an existing address phone string into canonical "+code national".
 * Falls back to auth parts when raw cannot yield a valid dial code.
 * @param {string|null|undefined} rawPhone
 * @param {{ authData?: object, masterCountries?: Array }} options
 * @returns {string}
 */
export const normalizeAddressPhoneString = (rawPhone, { authData, masterCountries = [] } = {}) => {
    if (!rawPhone) return '';

    const authParts = getAuthPhoneParts(authData, masterCountries);
    const parsed = parseAddressPhone(rawPhone, {
        masterCountries,
        fallbackCountryCode: authParts.countryCode || authData?.countryCode || '',
        fallbackPhoneCode: authParts.phoneCode || authData?.phoneCode || '',
    });

    const formatted = formatAddressPhone({
        phoneCode: parsed.phoneCode || authParts.phoneCode,
        national: parsed.phoneNumber || authParts.national,
    });

    return formatted;
};

/**
 * Resolve phone for cart customer.address writes.
 * Priority: address phone → existing valid cart phone → auth parts.
 * Never returns bare national-only auth phoneNumber.
 *
 * @param {{ addressPhone?: string, existingCartPhone?: string, authData?: object, masterCountries?: Array }} params
 * @returns {string}
 */
export const resolveCustomerAddressPhone = ({
    addressPhone,
    existingCartPhone,
    authData,
    masterCountries = [],
} = {}) => {
    if (addressPhone) {
        const fromAddress = normalizeAddressPhoneString(addressPhone, { authData, masterCountries });
        if (isValidAddressPhone(fromAddress)) {
            return fromAddress;
        }
    }

    if (existingCartPhone) {
        if (isValidAddressPhone(existingCartPhone)) {
            return normalizeAddressPhoneString(existingCartPhone, { authData, masterCountries }) || existingCartPhone;
        }
        const fromCart = normalizeAddressPhoneString(existingCartPhone, { authData, masterCountries });
        if (isValidAddressPhone(fromCart)) {
            return fromCart;
        }
    }

    const authParts = getAuthPhoneParts(authData, masterCountries);
    const fromAuth = formatAddressPhone({
        phoneCode: authParts.phoneCode,
        national: authParts.national,
    });
    if (isValidAddressPhone(fromAuth)) {
        return fromAuth;
    }

    return '';
};
